//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { useCallback, useEffect, useMemo, useRef } from 'react';

import { CHAT_PANEL_ID } from '@/components/Chat/chatPanelId';
import { useAuth } from '@/hooks/useAuth';
import { usePersistentState } from '@/hooks/usePersistentState';

// The id of the demo-data tool, which opens a menu rather than a panel and so
// has no panel id of its own to be known by.
export const DEMO_DATA_TOOL_ID = 'demo-data';

// The id of the server tool, which opens the settings pane of facts about the
// graph server. Like demo data, it opens something rather than being a panel.
export const SERVER_TOOL_ID = 'server';

// What a user finds pinned before they have chosen anything: the two tools
// every deployment has, whatever plugins it ships. Server is deliberately not
// among them -- it is a place to look something up rather than one to work
// from, so it waits in the Tools list for whoever wants it on the rail.
export const DEFAULT_PINNED_TOOL_IDS: string[] = [
  CHAT_PANEL_ID, DEMO_DATA_TOOL_ID
];

export interface PinnedTools {
  readonly isPinned: (id: string) => boolean;
  readonly setPinned: (id: string, next: boolean) => void;
}

/**
 * Which tools this user keeps on the taskbar.
 *
 * Stored as the whole list rather than as a set of deltas from the defaults,
 * and *absence of a record* is what means "has never chosen" -- so unpinning
 * a tool that ships pinned sticks, and pinning nothing at all is a real
 * choice rather than a reason to hand the defaults back. Storing a set of
 * overrides instead would make an empty list indistinguishable from an
 * untouched one, which is the same trap an absent `default_active` sets on
 * the backend.
 *
 * A new pin goes on the end, so pinning something never moves what is
 * already there.
 *
 * `validIds` and `ready` drive a one-time prune of ids that no longer name
 * anything: a plugin whose surfaces left the active manifest (removed, or
 * disabled by an admin -- the active-only config contract makes the two
 * indistinguishable) leaves a dangling id in storage forever otherwise, since
 * `isPinned` is the only reader and it just answers false for an id nothing
 * still owns. `ready` exists because the caller (Header) builds its tools
 * list from an async query; pruning against a `validIds` that has not
 * finished loading yet would delete pins for plugins that simply haven't
 * arrived. The prune fires once per mount, the first time `ready` is true --
 * not on every `validIds` change after that -- so a pin added later in that
 * mount is never re-litigated against a list that has since narrowed.
 *
 * `ready` is not the whole gate: storage is keyed by username
 * (`usePersistentState`), and on session restore the plugin-surfaces query
 * (which drives `ready`) can settle before the profile fetch that supplies
 * the username does. Pruning against that not-yet-keyed bucket would read
 * only defaults, find nothing stale, and still latch -- silently disabling
 * the real prune for the rest of the mount once the key does arrive. The
 * hook reads the profile itself and folds it into readiness so a caller
 * cannot forget this half of the gate.
 */
export const usePinnedTools = (
  validIds: readonly string[] = [], ready = false
): PinnedTools => {
  const { userProfile } = useAuth();
  // TODO: pins are a preference under the rule that preferences follow the
  // person, so they belong on `UserProfile` once the backend has a field for
  // them -- the prune below would then move to the profile write path.
  const [stored, setStored] =
    usePersistentState<string[] | null>('pinnedTools', null);

  const pinnedIds = stored ?? DEFAULT_PINNED_TOOL_IDS;

  const isPinned = useCallback(
    (id: string) => pinnedIds.includes(id), [pinnedIds]);

  const setPinned = useCallback((id: string, next: boolean) => {
    setStored((previous) => {
      const current = previous ?? DEFAULT_PINNED_TOOL_IDS;
      if (next)
        return current.includes(id) ? [...current] : [...current, id];
      return current.filter((pinned) => pinned !== id);
    });
  }, [setStored]);

  const hasPrunedRef = useRef(false);

  // Both halves of the gate, not just `ready`: see the doc comment above for
  // why a resolved username is as much a precondition as the query settling.
  const canPrune = ready && !!userProfile?.username;

  useEffect(() => {
    if (!canPrune || hasPrunedRef.current) return;
    hasPrunedRef.current = true;
    // The defaults are always valid, whether or not the caller's list names
    // them, so a deployment that ships with no tools yet loaded never loses
    // the pins nobody chose.
    const validSet = new Set([...DEFAULT_PINNED_TOOL_IDS, ...validIds]);
    setStored((previous) => {
      const current = previous ?? DEFAULT_PINNED_TOOL_IDS;
      const pruned = current.filter((id) => validSet.has(id));
      // Same length means nothing was stale: return the input reference so
      // usePersistentState's Object.is bail-out skips the render and the
      // storage write.
      return pruned.length === current.length ? previous : pruned;
    });
  }, [canPrune, validIds, setStored]);

  // One object per change of the pins themselves: the taskbar keys its
  // ranking memo on this value, and a fresh object per render would re-rank
  // -- and re-measure -- the rail on every render of the caller.
  return useMemo(() => ({ isPinned, setPinned }), [isPinned, setPinned]);
};
